import * as React from 'react';
import { ClientSDK, Platform } from '@wix/installation-manager-client-sdk';
import {
  AppPageMode,
  IAppMarketParams,
  IMarketplaceProps,
  IRoute,
  Marketplace,
  Origin,
  Path,
  PublicDevCenterBanner,
  StatusCode,
} from '@wix/marketplace-component';
import { Route } from 'react-router';
import queryString from 'query-string';
import * as jwt from 'jsonwebtoken';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { i18n as i18nType } from 'i18next';
import { showToast } from '../../utils/showToast';

interface IUrlObject {
  pathname: string;
  search?: string;
}

interface History {
  push(urlObject: IUrlObject): void;
}

interface IReactRouterInjected {
  match?: {
    params: {
      slug: string;
      path: string;
    };
  };
  location: any;
  history: History;
}

interface IAppProps extends Partial<InjectedExperimentsProps> {
  routeBaseName: string;
  locale: string;
  appMarketParams?: IAppMarketParams;
  marketplaceStaticsUrl: string;
  manageAppsStaticsUrl: string;
  baseURL?: string;
  ssrMemo?: any;
  isMobile?: boolean;
  i18n: i18nType;
  baseApiUrl?: string;
  metaTagsAggregator: string[];
  countryCode?: string;
  userName?: string;
  userImage?: string;
  status: { code: number };
  rpcClient: IMarketplaceProps['rpcClient'];
}

class App extends React.Component<IAppProps> {
  constructor(props) {
    super(props);
  }

  async componentDidMount() {
    if (
      !this.props.experiments?.enabled('specs.marketplace.includeFullstory')
    ) {
      // @ts-ignore
      window.wixRecorder.addLabel('app-market');
      // @ts-ignore
      window.wixRecorder.addLabel('app-market-sa');
    }
  }

  getUrlObject = (route: IRoute): string => {
    const getPathname = () => {
      if (route.path === Path.WEB_SOLUTION && route.slug) {
        return `${route.slug}`;
      }
      if (
        route.path === Path.CATEGORY &&
        route.subCategories &&
        Boolean(route.subCategories.length)
      ) {
        const subCatUri = route.subCategories.sort().join('__');
        return `${route.path}/${route.slug}/${subCatUri}`;
      }
      if (route.slug) {
        return `${route.path}/${route.slug}`;
      }
      return route.path;
    };

    const pathname = getPathname();
    const searchParams: Set<string> = new Set([]);
    [
      'query',
      'referral',
      'appIndex',
      'referralTag',
      'collimp_id',
      'referralSectionName',
    ].forEach(param => {
      if (
        (Boolean(route[param]) || param === 'appIndex') &&
        route[param] !== undefined &&
        !Number.isNaN(route[param])
      ) {
        searchParams.add(`${[param]}=${route[param]}`);
      }
    });

    // if (route.subCategories && Boolean(route.subCategories.length)) {
    //   searchParams.add(`subCat=${route.subCategories.join()}`);
    // }
    const search = Boolean(searchParams.size)
      ? `?${Array.from(searchParams.values()).join('&')}`
      : '';
    return `/${pathname}${search}`;
  };

  getMarketplaceProps = (
    history,
    currentRoute: IRoute,
    location: any,
  ): IMarketplaceProps => {
    const {
      locale,
      marketplaceStaticsUrl,
      manageAppsStaticsUrl,
      experiments,
      isMobile,
      i18n,
      baseApiUrl,
      countryCode,
      userName,
      userImage,
      status,
      rpcClient,
    } = this.props;

    const clientSDK = new ClientSDK(Platform.STANDALONE);
    const { siteId } = queryString.parse(location.search);
    if (siteId) {
      clientSDK.setMetaSiteId(siteId?.toString());
    }
    return {
      goto: (route: IRoute) => {
        const isBrowser = typeof window !== 'undefined';
        if (isBrowser) {
          window.scrollTo(0, 0);
        }
        history.push(this.getUrlObject(route));
      },
      responseCode: (code: StatusCode) => {
        const isBrowser = typeof window !== 'undefined';
        if (!isBrowser && status) {
          status.code = code;
        }
      },
      locale,
      baseApiUrl: baseApiUrl || 'https://www.wix.com',
      origin: Origin.STANDALONE,
      experimentsBag: experiments.all(),
      route: currentRoute,
      manageAppsStaticsUrl,
      marketplaceStaticsUrl,
      installApp: clientSDK && clientSDK.installApp,
      ssrMemo: this.props.ssrMemo || {},
      metaTagsAggregator: this.props.metaTagsAggregator,
      showHeader: currentRoute.path !== Path.SHARE,
      hideSearchInMenu: true,
      isMobile,
      i18n,
      countryCode,
      userName,
      userImage,
      showUserActionNotification: showToast,
      rpcClient,
    };
  };

  parseAppMarketParams = appMarketParamsKey => {
    let appMarketParams;
    try {
      appMarketParams = jwt.verify(
        appMarketParamsKey,
        'f225eecf-b12c-4c45-aeba-f5d35c19ce9b',
      );
      if (appMarketParams && typeof appMarketParams.data === 'string') {
        const parsedParams = JSON.parse(appMarketParams.data);
        return parsedParams.languageCode
          ? parsedParams
          : { ...parsedParams, languageCode: 'en' };
      }
    } catch (e) {
      console.log('error in appMarketParams');
    }

    return appMarketParams;
  };

  getSlug(slugParam: string, pathParam: string) {
    if (slugParam) {
      return slugParam;
    }
    const pathList = [
      Path.CATEGORY.toString(),
      Path.COLLECTION.toString(),
      Path.FINISH_SETUP.toString(),
      Path.HOME.toString(),
      Path.MANAGE_APPS.toString(),
      Path.RECOMMENDATION.toString(),
      Path.SEARCH.toString(),
      Path.SHARE.toString(),
      Path.WEB_SOLUTION.toString(),
      Path.DEVELOPER.toString(),
    ];
    if (!pathList.includes(pathParam)) {
      return pathParam;
    }
    return '';
  }

  getPath(path: string) {
    if (!path || path === 'main') {
      return Path.HOME;
    }
    switch (path) {
      case Path.CATEGORY.toString():
        return Path.CATEGORY;
      case Path.COLLECTION.toString():
        return Path.COLLECTION;
      case Path.SEARCH.toString():
        return Path.SEARCH;
      case Path.WEB_SOLUTION.toString():
        return Path.WEB_SOLUTION;
      case Path.SHARE.toString():
        return Path.SHARE;
      case Path.HOME.toString():
        return Path.HOME;
      case Path.DEVELOPER.toString():
        return Path.DEVELOPER;
      default:
        return Path.WEB_SOLUTION;
    }
  }

  getPathFromAppMarketParams(appMarketParams: any): IRoute {
    const appMarketParamsJson = this.parseAppMarketParams(appMarketParams);

    if (appMarketParamsJson) {
      const {
        route,
        shareId,
        appDefId,
        version,
        appId,
        languageCode,
      } = appMarketParamsJson;
      const Id = appDefId || appId; // :(
      if (Id && !route) {
        return {
          path: Path.FINISH_SETUP,
          slug: Id,
        };
      }

      if (Id && shareId && route === 'shareApp') {
        return {
          path: Path.SHARE,
          shareAppData: { appDefId: Id, shareId },
        };
      }
      if (Id && route === 'testApp') {
        return {
          path: Path.WEB_SOLUTION,
          slug: Id,
          version,
          appPageMode: AppPageMode.TEST,
          languageCode,
        };
      }

      if (Id && route === 'preview') {
        return {
          path: Path.WEB_SOLUTION,
          slug: Id,
          appPageMode: AppPageMode.PREVIEW,
          languageCode,
        };
      }
    }
    return { path: Path.HOME };
  }

  calcRoute(match: any, location: any): IRoute {
    const {
      query,
      referral,
      subCat,
      appMarketParams,
      AppMarketParams,
      platformName,
      appIndex,
      referralTag,
      collimp_id,
      referralSectionName,
    } = queryString.parse(location.search);

    if (appMarketParams || AppMarketParams) {
      return this.getPathFromAppMarketParams(
        appMarketParams || AppMarketParams,
      );
    }
    const path: Path = this.getPath(match.params.path);
    const getSubcategories = () => {
      if (path === Path.CATEGORY && match.params.subslug) {
        return match.params.subslug.split('__');
      }
    };

    const subCategories = getSubcategories();
    const subDomain = this.props.locale === 'en' ? 'www' : this.props.locale;
    const currentUrl = `https://${subDomain}.wix.com/app-market${location.pathname}${location.search}`;
    const slug = this.getSlug(match.params.slug, match.params.path);

    return {
      path,
      slug,
      query: query as string,
      referral: referral as string,
      subCategories,
      platformName: platformName as string,
      currentUrl,
      appIndex: Number(appIndex),
      referralTag: referralTag as string,
      collimp_id: collimp_id as string,
      referralSectionName: referralSectionName as string,
    };
  }

  render() {
    return (
      <Route
        path="/:path?/:slug?/:subslug?"
        component={({ match, location, history }: IReactRouterInjected) => {
          const route = this.calcRoute(match, location);
          const path = this.getPath(route.path);

          return (
            <>
              <Marketplace
                {...this.getMarketplaceProps(history, route, location)}
                baseUrl={this.props.baseURL}
              />
              {path !== Path.SHARE && (
                <PublicDevCenterBanner t={k => this.props.i18n.t(k)} />
              )}
            </>
          );
        }}
      />
    );
  }
}

export default withExperiments(App);
